import axiosInstance from '../../services/axiosInstance';
import {
  ProductImageValidationJob,
  ProductImageValidationRun,
  ProductImageValidationRunSummary,
  ProductImageValidationSettings,
  ProductImageValidationCategoryRoot,
  ProductImageValidationScope,
} from './types';

function getFilenameFromDisposition(disposition?: string, fallback = 'download'): string {
  if (!disposition) return fallback;
  const match = disposition.match(/filename="?([^";]+)"?/i);
  return match ? decodeURIComponent(match[1]) : fallback;
}

export async function startProductImageValidationRun(
  scope: ProductImageValidationScope,
  assetIds?: string[],
  categoryRoot?: ProductImageValidationCategoryRoot,
): Promise<{ jobId: string; totalAssets: number }> {
  const { data } = await axiosInstance.post('/api/assets/product-image-validation/run', {
    scope,
    ...(scope === 'selected' ? { assetIds: assetIds || [] } : {}),
    ...(scope === 'all' && categoryRoot ? { categoryRoot } : {}),
  });

  return {
    jobId: String(data.jobId),
    totalAssets: Number(data.totalAssets || 0),
  };
}

export async function getProductImageValidationStatus(jobId: string): Promise<ProductImageValidationJob> {
  const { data } = await axiosInstance.get('/api/assets/product-image-validation/status', {
    params: { jobId },
  });
  return data.job as ProductImageValidationJob;
}

export async function getProductImageValidationSettings(): Promise<ProductImageValidationSettings> {
  const { data } = await axiosInstance.get('/api/assets/product-image-validation/settings');
  return data.settings as ProductImageValidationSettings;
}

export async function saveProductImageValidationSettings(settings: ProductImageValidationSettings): Promise<ProductImageValidationSettings> {
  const { data } = await axiosInstance.put('/api/assets/product-image-validation/settings', settings);
  return data.settings as ProductImageValidationSettings;
}

export async function listProductImageValidationRuns(limit = 20): Promise<ProductImageValidationRunSummary[]> {
  const { data } = await axiosInstance.get('/api/assets/product-image-validation/runs', {
    params: { limit },
  });
  return Array.isArray(data.runs) ? data.runs as ProductImageValidationRunSummary[] : [];
}

export async function getProductImageValidationRun(runId: string): Promise<ProductImageValidationRun> {
  const { data } = await axiosInstance.get(`/api/assets/product-image-validation/runs/${encodeURIComponent(runId)}`);
  return data.run as ProductImageValidationRun;
}

export async function downloadProductImageValidationRun(
  runId: string,
  format: 'json' | 'csv' | 'txt',
): Promise<void> {
  const response = await axiosInstance.get(
    `/api/assets/product-image-validation/runs/${encodeURIComponent(runId)}/download`,
    {
      params: { format },
      responseType: 'blob',
    },
  );

  const contentType = response.headers['content-type'] || 'application/octet-stream';
  const disposition = response.headers['content-disposition'] || '';
  const fallbackFilename = `product-image-validation-${runId}.${format}`;
  const filename = getFilenameFromDisposition(disposition, fallbackFilename);
  const blob = new Blob([response.data], { type: contentType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
